import AccountIcon from "@/components/AccountIcon";
import type {
  Award,
  Certification,
  CustomSection,
  CustomSectionGroup,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  SectionWithItem,
  Skill,
  Volunteer,
} from "@/lib/schema/sections";
import { TResume } from "@/lib/store/resume";
import { onPreviewSectionClick } from "@/lib/utils/resumeBuilder";
import { isUrl } from "@/utils";
import { isEmptyString } from "@/utils/preview";
import { Link as LinkIcon, Mail, MapPin, Phone } from "lucide-react";
import { Fragment } from "react";
import { TemplateProps } from ".";
import { Picture } from "../sidebars/left/sections/shared/Picture";

const Header = ({ resume }: { resume: TResume }) => {
  const basics = resume.data.basics;

  if (!basics) return null;

  return (
    <div
      onClick={() =>
        onPreviewSectionClick(`builder-basics`, {
          highlight: false,
          accordion: false,
        })
      }
      className="flex flex-col items-center justify-center space-y-2 pb-2 text-center"
    >
      <Picture resume={resume} />

      <div>
        <div className="text-2xl font-bold">{basics.name}</div>
        <div className="text-base">{basics.headline}</div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 text-sm">
        {basics.location && (
          <div className="flex items-center gap-x-1.5">
            <MapPin
              className="resume-icon text-resume-primary"
              strokeWidth={2.5}
              size={16}
            />
            <div>{basics.location}</div>
          </div>
        )}
        {basics.phone && (
          <div className="flex items-center gap-x-1.5">
            <Phone
              className="resume-icon text-resume-primary"
              strokeWidth={2.5}
              size={16}
            />
            <a href={`tel:${basics.phone}`} target="_blank" rel="noreferrer">
              {basics.phone}
            </a>
          </div>
        )}
        {basics.email && (
          <div className="flex items-center gap-x-1.5">
            <Mail
              className="resume-icon text-resume-primary"
              strokeWidth={2.5}
              size={16}
            />
            <a href={`mailto:${basics.email}`} target="_blank" rel="noreferrer">
              {basics.email}
            </a>
          </div>
        )}
        {basics?.customFields?.map((item) => (
          <div key={item.id} className="flex items-center gap-x-1.5">
            <AccountIcon
              name={item.icon}
              strokeWidth={2.5}
              className="resume-icon text-resume-primary"
              size={16}
            />
            <span>{[item.name, item.value].filter(Boolean).join(": ")}</span>
          </div>
        ))}
        {basics.website?.href && (
          <div className="flex items-center gap-x-1.5">
            <LinkIcon
              className="resume-icon text-resume-primary"
              strokeWidth={2.5}
              size={16}
            />
            <a target="_blank" rel="noreferrer">
              {basics.website?.href}
            </a>
          </div>
        )}

        {/* {basics.customFields.map((item) => (
          <div key={item.id} className="flex items-center gap-x-1.5">
            <i className={cn(`ph ph-bold ph-${item.icon}`, "text-resume-primary")} />
            <span>{[item.name, item.value].filter(Boolean).join(": ")}</span>
          </div>
        ))} */}
      </div>
    </div>
  );
};

const Summary = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.summary;

  if (!section?.visible || isEmptyString(section?.content)) return null;

  return (
    <section
      id={section?.id}
      className="section-item"
      onClick={() => onPreviewSectionClick(`builder-${section?.id}`)}
    >
      <div className="text-resume-primary mb-2 hidden text-lg font-bold group-[.main]:block">
        <h4>{section?.name}</h4>
      </div>

      <div className="text-resume-primary mb-2 hidden items-center gap-x-2 text-center font-bold group-[.sidebar]:flex">
        <div className="border-resume-primary h-1.5 w-1.5 rounded-full border" />
        <h4>{section?.name}</h4>
        <div className="border-resume-primary h-1.5 w-1.5 rounded-full border" />
      </div>

      <main className="border-resume-primary relative space-y-2 border-l pl-4">
        <div className="bg-resume-primary absolute left-[-4.5px] top-[8px] hidden h-[8px] w-[8px] rounded-full group-[.main]:block" />

        <div dangerouslySetInnerHTML={{ __html: section?.content || "" }} />
      </main>
    </section>
  );
};

type SectionProps<T> = {
  section?: SectionWithItem<T> | CustomSectionGroup;
  children?: (item: T) => React.ReactNode;
  className?: string;
  urlKey?: keyof T;
  levelKey?: keyof T;
  summaryKey?: keyof T;
  keywordsKey?: keyof T;
};

const Section = <T,>({
  section,
  children,
  className,
  urlKey,
  levelKey,
  summaryKey,
  keywordsKey,
}: SectionProps<T>) => {
  if (!section?.visible || !section?.items?.length) return null;
  return (
    <section
      id={section.id}
      className="section-item grid"
      onClick={() =>
        onPreviewSectionClick(`builder-${section?.id}-${section?.items[0]?.id}`)
      }
    >
      <div className="text-resume-primary mb-2 hidden text-lg font-bold group-[.main]:block">
        <h4>{section.name}</h4>
      </div>

      <div className="text-resume-primary mx-auto mb-2 hidden items-center gap-x-2 text-center font-bold group-[.sidebar]:flex">
        <div className="border-resume-primary h-1.5 w-1.5 rounded-full border" />
        <h4>{section.name}</h4>
        <div className="border-resume-primary h-1.5 w-1.5 rounded-full border" />
      </div>

      <div
        className="grid gap-x-6 gap-y-3 group-[.sidebar]:mx-auto group-[.sidebar]:text-center"
        style={{ gridTemplateColumns: `repeat(1, 1fr)` }}
      >
        {section.items
          .filter((item: any) => item.visible)
          .map((item: any) => {
            const url = item[urlKey];
            const level = item[levelKey];
            const summary = item?.summary;
            const keywords = item[keywordsKey];

            return (
              <div
                key={item.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviewSectionClick(`builder-${section?.id}-${item.id}`);
                }}
                className="border-resume-primary relative space-y-2 group-[.main]:border-l group-[.main]:pl-4"
              >
                {children?.(item as T) && <div>{children?.(item as T)}</div>}

                {summary !== undefined && !isEmptyString(summary) && (
                  <div dangerouslySetInnerHTML={{ __html: summary }} />
                )}

                {level !== undefined && level > 0 && level}

                {keywords !== undefined && keywords.length > 0 && (
                  <p className="text-sm">{keywords.join(", ")}</p>
                )}

                {url?.href && (
                  <a target="_blank" rel="noreferrer" href={url?.href}>
                    {url?.href}
                  </a>
                )}

                <div className="bg-resume-primary absolute left-[-4.5px] top-px hidden h-[8px] w-[8px] rounded-full group-[.main]:block" />
              </div>
            );
          })}
      </div>
    </section>
  );
};

const WorkExperience = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.workExperience;

  return (
    <Section<Experience> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.company}</div>
          <div>{item.position}</div>
          <div>{item.location}</div>
          <div className="font-bold">{item.date}</div>
        </div>
      )}
    </Section>
  );
};

const Internship = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.internship;

  return (
    <Section<Experience> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.company}</div>
          <div>{item.position}</div>
          <div>{item.location}</div>
          <div className="font-bold">{item.date}</div>
        </div>
      )}
    </Section>
  );
};

const Projects = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.projects;

  return (
    <Section<Project>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
    >
      {(item) => (
        <div>
          <div>
            <div className="text-base font-bold">{item.name}</div>
            <div>{item.description}</div>

            <div className="font-bold">{item.date}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const Skills = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.skills;
  return (
    <Section<Skill> section={section} levelKey="level" keywordsKey="keywords">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.name}</div>
          {/* <div>{item.description}</div> */}
        </div>
      )}
    </Section>
  );
};

const Education = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.education;

  return (
    <Section<Education> section={section} summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.institution}</div>
          <div>{item.major}</div>
          <div>{item.score}</div>
          <div>{item.studyType}</div>
          <div>{item.officialSite}</div>
          <div className="font-bold">{item.date}</div>
        </div>
      )}
    </Section>
  );
};

const Custom = ({ id, resume }: { id: string; resume: TResume }) => {
  const section = resume.data.sections?.custom?.[id];
  return (
    <Section<CustomSection>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
    >
      {(item) => {
        if (!item.name && !item.description && !item.date && !item.location) {
          return null;
        }
        return (
          <div>
            <div>
              <div className="text-base font-bold">{item.name}</div>
              <div>{item.description}</div>

              <div className="font-bold">{item.date}</div>
              <div>{item.location}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const Awards = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.awards;

  return (
    <Section<Award> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.title}</div>
          <div>{item.awarder}</div>
          <div className="font-bold">{item.date}</div>
        </div>
      )}
    </Section>
  );
};

const Certifications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.certifications;

  return (
    <Section<Certification> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.issuer}</div>
          <div className="font-bold">{item.date}</div>
        </div>
      )}
    </Section>
  );
};

const Interests = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.interests;

  return (
    <Section<Interest>
      section={section}
      keywordsKey="keywords"
      className="space-y-0.5"
    >
      {(item) => <div className="text-base font-bold">{item.name}</div>}
    </Section>
  );
};

const Publications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.publications;
  return (
    <Section<Publication> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.publisher}</div>
          <div className="font-bold">{item.date}</div>
        </div>
      )}
    </Section>
  );
};

const Volunteer = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.volunteer;

  return (
    <Section<Volunteer> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.organization}</div>
          <div>{item.position}</div>
          <div>{item.location}</div>
          <div className="font-bold">{item.date}</div>
        </div>
      )}
    </Section>
  );
};

const Languages = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.languages;

  return (
    <Section<Language> section={section} levelKey="level">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.description}</div>
        </div>
      )}
    </Section>
  );
};

const Profiles = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.profiles;
  const fontSize = resume.data.settings?.typography.font.size;

  return (
    <Section<Profile> section={section}>
      {(item) => (
        <div>
          {isUrl(item.url?.href) ? (
            <a href={item.url?.href} className="text-resume-primary">
              <img
                className="resume-icon mr-1"
                width={fontSize}
                height={fontSize}
                alt={item.network}
                src={`https://cdn.simpleicons.org/${item.icon}`}
              />
              {item.username}
            </a>
          ) : (
            <p>{item.username}</p>
          )}
          {item.url?.href && (
            <a
              className="block text-sm"
              target="_blank"
              rel="noreferrer"
              href={item.url?.href}
            >
              {item.url?.href}
            </a>
          )}
        </div>
      )}
    </Section>
  );
};

const References = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.references;

  return (
    <Section<Reference> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.description}</div>
        </div>
      )}
    </Section>
  );
};

const mapSectionToComponent = (section: any, resume: TResume) => {
  switch (section) {
    case "profiles":
      return <Profiles resume={resume} />;
    case "summary":
      return <Summary resume={resume} />;
    case "workExperience":
      return <WorkExperience resume={resume} />;
    case "internship":
      return <Internship resume={resume} />;
    case "education":
      return <Education resume={resume} />;
    case "awards":
      return <Awards resume={resume} />;
    case "certifications":
      return <Certifications resume={resume} />;
    case "skills":
      return <Skills resume={resume} />;
    case "interests":
      return <Interests resume={resume} />;
    case "publications":
      return <Publications resume={resume} />;
    case "volunteer":
      return <Volunteer resume={resume} />;
    case "languages":
      return <Languages resume={resume} />;
    case "projects":
      return <Projects resume={resume} />;
    case "references":
      return <References resume={resume} />;
    default:
      if (section.startsWith("custom."))
        return <Custom id={section.split(".")[1]} resume={resume} />;

      return null;
  }
};

export const Azurill = ({
  resume,
  columns,
  isFirstPage = false,
}: TemplateProps) => {
  const [main, sidebar] = columns;
  return (
    <div className="space-y-3 p-custom">
      {isFirstPage && <Header resume={resume} />}
      <div className="flex gap-4">
        <div className="sidebar group w-[30%] space-y-4">
          {sidebar.map((section) => (
            <Fragment key={section}>
              {mapSectionToComponent(section, resume)}
            </Fragment>
          ))}
        </div>
        <div className="main group col-span-2 w-[70%] space-y-4">
          {main.map((section) => (
            <Fragment key={section}>
              {mapSectionToComponent(section, resume)}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};
